import React from "react";
import SwitchToDesktop from "@/shared/ui/SwitchToDesktop/SwitchToDesktop";
import { useState, useEffect } from "react";
import { useMe } from "@/Hooks";
import { useLocation, useNavigate } from "react-router-dom";

interface MobileAwareLayoutProps {
  children: React.ReactNode;
}

const useMobileDetection = () => {
  const [shouldShowMobileOverlay, setShouldShowMobileOverlay] = useState(false);
  const { me, loading } = useMe();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const isMobileDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent.toLowerCase(),
      );
    };

    const handleMobileDetection = () => {
      const isMobile = isMobileDevice();
      const isMobileWidth = window.innerWidth < 550;
      const currentPath = location.pathname;

      // List of routes that should NOT be redirected on mobile
      const allowedMobileRoutes = [
        "/landing-mobile",
        "/desktop",
        "/about-us",
        "/free-tools",
        "/privacy",
        "/terms",
        "/copyright",
        "/apps",
        "/login",
        "/register",
        "/newregister",
        "/reset",
        "/verify",
        "/oauth",
        "/error",
        "/verify-email",
        "/reset",
        "/join",
      ];

      // Check if current path starts with any of the allowed routes
      const isAllowedRoute = allowedMobileRoutes.some(
        (route) => currentPath === route || currentPath.startsWith(route + "/"),
      );

      // Check if current path matches /apps/:slug/ pattern
      const isAppVideoPage = /^\/apps\/[^\/]+\/?$/.test(currentPath);

      // Only redirect if we're not on an allowed route and not loading
      if (!isAllowedRoute && !isAppVideoPage && !loading) {
        if (isMobile && isMobileWidth && !me) {
          navigate("/landing-mobile/");
        } else if (isMobile && isMobileWidth && me) {
          setShouldShowMobileOverlay(true);
        } else {
          setShouldShowMobileOverlay(false);
        }
      } else {
        setShouldShowMobileOverlay(false);
      }
    };

    handleMobileDetection();
    window.addEventListener("resize", handleMobileDetection);

    return () => window.removeEventListener("resize", handleMobileDetection);
  }, [me, loading, location.pathname, navigate]);

  return shouldShowMobileOverlay;
};

const MobileAwareLayout: React.FC<MobileAwareLayoutProps> = ({ children }) => {
  const shouldShowMobileOverlay = useMobileDetection();

  return (
    <>
      {/* Always render children for SEO */}
      <div
        style={{
          display: shouldShowMobileOverlay ? "none" : "block",
        }}
      >
        {children}
      </div>

      {/* Show mobile overlay when needed - only for authenticated users */}
      {shouldShowMobileOverlay && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999,
            backgroundColor: "white",
          }}
        >
          <SwitchToDesktop />
        </div>
      )}
    </>
  );
};

export default MobileAwareLayout;
